import type { PipelineStepId, PipelineStepState, StepStatus } from '../types';
import { PIPELINE_STEP_IDS } from '../types';
import { summarizeStepOutput } from '../utils';

type RecordObject = Record<string, unknown>;

function asRecord(value: unknown): RecordObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordObject)
    : null;
}

export const STEP_ACCENTS: Record<PipelineStepId, string> = {
  lin: '#1E6DF6',
  adie: '#EC7E00',
  ics: '#00A87E',
  de: '#7C5CFF',
};

export function countStepInput(input: unknown): number | null {
  if (input === null || input === undefined) {
    return null;
  }

  if (Array.isArray(input)) {
    return input.length;
  }

  const record = asRecord(input);
  if (!record) {
    return 1;
  }

  if (Array.isArray(record.logs)) {
    return record.logs.length;
  }

  if (Array.isArray(record.anomaly_events)) {
    return record.anomaly_events.length;
  }

  if (Array.isArray(record.events)) {
    return record.events.length;
  }

  return 1;
}

export type FriendlySummary =
  | { kind: 'key'; key: string; params?: Record<string, string | number> }
  | { kind: 'text'; text: string };

export function buildFriendlySummary(
  stepId: PipelineStepId,
  step: PipelineStepState,
): FriendlySummary {
  if (step.status === 'idle' || step.status === 'ready') {
    const count = countStepInput(step.input);
    if (count !== null && count > 0) {
      return { kind: 'key', key: 'summaries.itemsReady', params: { count } };
    }
    return { kind: 'key', key: 'summaries.waitingForLogs' };
  }

  if (step.status === 'running') {
    if (step.streamTotal && step.streamTotal > 0) {
      const completed = new Set(
        (step.streamItems ?? []).map((item) => item.index),
      ).size;
      return {
        kind: 'key',
        key: 'summaries.streamingProgress',
        params: { completed, total: step.streamTotal },
      };
    }

    return { kind: 'key', key: 'summaries.processing' };
  }

  if (step.status === 'error') {
    return { kind: 'text', text: step.error ?? 'Something went wrong' };
  }

  if (!step.output) {
    return { kind: 'key', key: 'summaries.done' };
  }

  if (stepId === 'lin' && Array.isArray(step.output)) {
    return {
      kind: 'key',
      key: 'summaries.logsNormalized',
      params: { count: step.output.length },
    };
  }

  if (stepId === 'adie' && asRecord(step.output)) {
    const routing = asRecord(asRecord(step.output)?.routing);
    const forwarded = routing?.forwardedToAdie;
    const skipped = routing?.skippedNoTelemetries;
    if (typeof forwarded === 'number') {
      return {
        kind: 'key',
        key: 'summaries.adieRouting',
        params: {
          forwarded,
          skipped: typeof skipped === 'number' ? skipped : 0,
        },
      };
    }
  }

  if (stepId === 'ics' && asRecord(step.output)) {
    const outputRecord = asRecord(step.output);
    if (outputRecord?.skipped === true) {
      return { kind: 'key', key: 'summaries.icsSkipped' };
    }

    const routing = asRecord(outputRecord?.routing);
    const forwarded = routing?.forwardedToIcs;
    const anomalies = routing?.anomalyEvents;
    if (typeof forwarded === 'number' && typeof anomalies === 'number') {
      return {
        kind: 'key',
        key: 'summaries.icsRouting',
        params: { logs: forwarded, anomalies },
      };
    }
  }

  if (stepId === 'adie' && Array.isArray(step.output)) {
    const anomalies = step.output.reduce((total, item) => {
      const record = asRecord(item) ?? {};
      const events = record.anomaly_event ?? record.anomalyEvent;
      return total + (Array.isArray(events) ? events.length : 0);
    }, 0);
    return anomalies > 0
      ? {
          kind: 'key',
          key: 'summaries.anomaliesFlagged',
          params: { count: anomalies },
        }
      : { kind: 'key', key: 'summaries.noAnomalies' };
  }

  if (stepId === 'ics') {
    const record = asRecord(step.output) ?? {};
    const results = Array.isArray(record.results) ? record.results : [];
    const incident = asRecord(asRecord(results[0])?.incident);
    const severity = asRecord(incident?.severity)?.level;
    const priority = asRecord(incident?.priority)?.level;
    if (severity && priority) {
      return {
        kind: 'key',
        key: 'summaries.incidentSeverity',
        params: { severity: String(severity), priority: String(priority) },
      };
    }
    return {
      kind: 'key',
      key: 'summaries.incidentUpdates',
      params: { count: results.length },
    };
  }

  const diagnosis = asRecord(step.output) ?? {};
  const rootCause = asRecord(diagnosis.rootCauseAnalysis)?.rootCause;
  if (typeof rootCause === 'string') {
    return { kind: 'text', text: rootCause };
  }

  return { kind: 'text', text: summarizeStepOutput(stepId, step.output) };
}

export function formatFriendlySummary(
  summary: FriendlySummary,
  translate: (key: string, params?: Record<string, string | number>) => string,
): string {
  if (summary.kind === 'text') {
    return summary.text;
  }
  return translate(summary.key, summary.params);
}

export function statusLabelKey(status: StepStatus): string {
  return `status.${status}`;
}

export function totalUploadedRows(
  uploads: Array<{ records: unknown[] }>,
): number {
  return uploads.reduce((total, upload) => total + upload.records.length, 0);
}

export function completedStepCount(
  steps: Record<PipelineStepId, PipelineStepState>,
): number {
  return PIPELINE_STEP_IDS.filter((id) => steps[id].status === 'completed')
    .length;
}
